import React, { useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { ChevronUp, ChevronDown, Clock, History, Award } from 'lucide-react';

export const PrecisionTimePicker = ({ value, onChange, lastSessionTime, mostChosenTime }) => {
  // value is "HH:mm"
  const [hours, setHours] = useState(12);
  const [minutes, setMinutes] = useState(0);
  const [isPM, setIsPM] = useState(true);
  const hourInputRef = useRef(null);
  const minuteInputRef = useRef(null);

  useEffect(() => {
    if (value) {
      const [h, m] = value.split(':').map(Number);
      if (h >= 12) {
        setHours(h === 12 ? 12 : h - 12);
        setIsPM(true);
      } else {
        setHours(h === 0 ? 12 : h);
        setIsPM(false);
      }
      setMinutes(m);
    }
  }, [value]);

  const updateParent = (h, m, pm) => {
    let finalH = h;
    if (pm) {
      if (h < 12) finalH = h + 12;
    } else {
      if (h === 12) finalH = 0;
    }
    const formatted = `${String(finalH).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    onChange(formatted);
  };

  const handleHourChange = (newH) => {
    let val = Math.max(1, Math.min(12, newH));
    setHours(val);
    updateParent(val, minutes, isPM);
  };

  const handleMinuteChange = (newM) => {
    // Snap logic: if called via arrows, we can step 30. If typed, we allow any.
    // However, the user said "every 30 minutes as a start time. and if they want to input a custom one they can."
    let val = newM;
    if (val >= 60) val = 0;
    if (val < 0) val = 30; // Loop back from 0 to 30/59? Let's say 45 or 30.
    setMinutes(val);
    updateParent(hours, val, isPM);
  };

  const stepMinutes = (dir) => {
    let newM = minutes + (dir * 30);
    if (newM >= 60) newM = 0;
    if (newM < 0) newM = 30;
    handleMinuteChange(newM);
  };

  const togglePM = () => {
    setIsPM(!isPM);
    updateParent(hours, minutes, !isPM);
  };

  const applyPreset = (timeStr) => {
    if (!timeStr) return;
    onChange(timeStr);
  };

  return (
    <div className="flex flex-col items-center gap-8 w-full max-w-sm mx-auto p-6 bg-slate-900/40 border border-white/5 rounded-[2.5rem] backdrop-blur-xl transition-all duration-500">
      
      {/* Digital Input Group */}
      <div className="flex items-center justify-center gap-4 select-none" dir="ltr">
        {/* Hours Selector */}
        <div className="flex flex-col items-center gap-2">
          <button 
            onClick={() => handleHourChange(hours + 1 > 12 ? 1 : hours + 1)}
            className="p-2 text-slate-500 hover:text-amber-400 transition-colors"
          >
            <ChevronUp size={24} />
          </button>
          <div className="relative group">
            <input
              ref={hourInputRef}
              type="number"
              value={String(hours).padStart(2, '0')}
              onChange={(e) => handleHourChange(parseInt(e.target.value) || 0)}
              className="w-20 py-4 bg-slate-950/50 border border-white/10 rounded-2xl text-4xl font-black text-center text-white outline-none focus:border-amber-500/50 focus:ring-4 focus:ring-amber-500/10 transition-all font-mono"
            />
          </div>
          <button 
            onClick={() => handleHourChange(hours - 1 < 1 ? 12 : hours - 1)}
            className="p-2 text-slate-500 hover:text-amber-400 transition-colors"
          >
            <ChevronDown size={24} />
          </button>
        </div>

        <div className="text-4xl font-black text-slate-700 mt-2">:</div>

        {/* Minutes Selector */}
        <div className="flex flex-col items-center gap-2">
          <button 
            onClick={() => stepMinutes(1)}
            className="p-2 text-slate-500 hover:text-amber-400 transition-colors"
          >
            <ChevronUp size={24} />
          </button>
          <div className="relative group">
            <input
              ref={minuteInputRef}
              type="number"
              value={String(minutes).padStart(2, '0')}
              onChange={(e) => handleMinuteChange(parseInt(e.target.value) || 0)}
              className="w-20 py-4 bg-slate-950/50 border border-white/10 rounded-2xl text-4xl font-black text-center text-white outline-none focus:border-amber-500/50 focus:ring-4 focus:ring-amber-500/10 transition-all font-mono"
            />
          </div>
          <button 
            onClick={() => stepMinutes(-1)}
            className="p-2 text-slate-500 hover:text-amber-400 transition-colors"
          >
            <ChevronDown size={24} />
          </button>
        </div>

        {/* AM/PM Toggle */}
        <div className="flex flex-col h-full self-stretch justify-center ml-2">
          <button 
            onClick={togglePM}
            className={clsx(
              "px-4 py-3 rounded-2xl text-sm font-black transition-all border-2",
              isPM 
                ? "bg-indigo-600/20 border-indigo-500 text-indigo-400 shadow-lg shadow-indigo-500/20" 
                : "bg-amber-500/20 border-amber-500 text-amber-500 shadow-lg shadow-amber-500/20"
            )}
          >
            {isPM ? 'PM' : 'AM'}
          </button>
        </div>
      </div>

      {/* Smart Presets */}
      <div className="w-full space-y-4">
        <div className="flex items-center gap-2 px-2">
          <div className="h-px flex-1 bg-white/5" />
          <span className="text-[10px] text-slate-500 uppercase font-black tracking-widest">Smart Presets</span>
          <div className="h-px flex-1 bg-white/5" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => applyPreset(lastSessionTime)}
            disabled={!lastSessionTime}
            className={clsx(
              "group relative overflow-hidden p-4 rounded-2xl border transition-all duration-300",
              "bg-slate-800/40 border-white/5 hover:border-amber-500/50 active:scale-95 disabled:opacity-50"
            )}
          >
            <div className="relative z-10 flex flex-col items-center gap-1">
              <History size={16} className="text-slate-500 group-hover:text-amber-400 transition-colors" />
              <span className="text-[10px] font-black text-slate-500 uppercase">Last Session</span>
              <span className="text-sm font-bold text-white tabular-nums">{lastSessionTime || '--:--'}</span>
            </div>
          </button>

          <button
            onClick={() => applyPreset(mostChosenTime)}
            disabled={!mostChosenTime}
            className={clsx(
              "group relative overflow-hidden p-4 rounded-2xl border transition-all duration-300",
              "bg-slate-800/40 border-white/5 hover:border-amber-500/50 active:scale-95 disabled:opacity-50"
            )}
          >
            <div className="relative z-10 flex flex-col items-center gap-1">
              <Award size={16} className="text-slate-500 group-hover:text-amber-400 transition-colors" />
              <span className="text-[10px] font-black text-slate-500 uppercase">Most Chosen</span>
              <span className="text-sm font-bold text-white tabular-nums">{mostChosenTime || '--:--'}</span>
            </div>
          </button>
        </div>
        
        {/* Quick Snaps */}
        <div className="flex gap-2 justify-center">
            {['20:00', '21:00', '22:00'].map(snap => (
                <button 
                  key={snap}
                  onClick={() => applyPreset(snap)}
                  className="px-4 py-2 rounded-xl bg-slate-800/20 border border-white/5 text-[11px] font-bold text-slate-400 hover:text-white hover:border-amber-500/30 transition-all"
                >
                    {snap}
                </button>
            ))}
        </div>
      </div>
    </div>
  );
};
